import { useEffect, useState } from "react";
import WebSocketService from "../../../services/WebSocketService";
import { getGlobalMonitoringData } from "../../../api/globalMonitoringService";

function GlobalMonitoringCenterPage() {
    const [data, setData] = useState(null);
    const [liveEvents, setLiveEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);

            try {
                const result = await getGlobalMonitoringData();
                setData(result);
            } catch (err) {
                console.error("Global Monitoring load failed", err);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    useEffect(() => {
        const ws = new WebSocketService();

        ws.onMessage = (raw) => {
            try {
                const msg = JSON.parse(raw);

                setLiveEvents((prev) =>
                    [
                        {
                            id: `${Date.now()}-${Math.random()}`,
                            type: msg.type || "Live Event",
                            message: msg.message || msg.trackingNumber || "System update received",
                            time: new Date().toLocaleTimeString(),
                        },
                        ...prev,
                    ].slice(0, 10)
                );
            } catch (err) {
                console.error("Global Monitoring WS error", err);
            }
        };

        ws.connect();

        return () => {
            if (typeof ws.close === "function") ws.close();
            else if (typeof ws.disconnect === "function") ws.disconnect();
        };
    }, []);

    if (loading) {
        return <div style={{ padding: 20 }}>Loading Global Monitoring Center...</div>;
    }

    if (!data) {
        return <div style={{ padding: 20 }}>Monitoring data not available.</div>;
    }

    return (
        <div style={{ padding: 20 }}>
            <h2>Global Monitoring Center</h2>

            <p style={{ color: "#666" }}>
                Unified real-time monitoring for shipments, IoT, blockchain, AI, security and logistics operations.
            </p>

            <div style={gridStyle}>
                <Card title="Active Shipments" value={data.activeShipments} color="#1976D2" />
                <Card title="Active Orders" value={data.activeOrders} color="#43A047" />
                <Card title="IoT Online" value={`${data.onlineDevices}/${data.iotDevices}`} color="#7B1FA2" />
                <Card title="Blockchain Events" value={data.blockchainEvents} color="#546E7A" />
                <Card title="Notifications" value={data.notificationsCount} color="#EF6C00" />
                <Card title="Critical Events" value={data.criticalEvents} color="#C62828" />
                <Card title="AI Risk" value={`${data.aiRisk}%`} color="#D84315" />
                <Card title="Trust Score" value={`${data.trustScore}%`} color="#2E7D32" />
            </div>

            <div style={layoutStyle}>
                <Panel title="Live IoT Devices">
                    {data.devices.length === 0 ? (
                        <p>No IoT devices found.</p>
                    ) : (
                        data.devices.slice(0, 8).map((d) => (
                            <Row
                                key={d.id || d.trackingNumber}
                                title={d.trackingNumber || d.name || "IoT Device"}
                                subtitle={`${d.latitude ?? "�"}, ${d.longitude ?? "�"}`}
                                status={isOnline(d) ? "Online" : "Offline"}
                                ok={isOnline(d)}
                            />
                        ))
                    )}
                </Panel>

                <Panel title="Active Shipments">
                    {data.shipments.length === 0 ? (
                        <p>No shipments found.</p>
                    ) : (
                        data.shipments.slice(0, 8).map((s) => (
                            <Row
                                key={s.id}
                                title={s.trackingNumber || `Shipment #${s.id}`}
                                subtitle={s.status || s.shipmentStatus || "�"}
                                status="Active"
                                ok
                            />
                        ))
                    )}
                </Panel>

                <Panel title="Live Events">
                    {liveEvents.length === 0 ? (
                        <p style={{ color: "#777" }}>Waiting for live events...</p>
                    ) : (
                        liveEvents.map((e) => (
                            <div key={e.id} style={eventStyle}>
                                <b>{e.type}</b>
                                <br />
                                {e.message}
                                <br />
                                <span style={{ color: "#777", fontSize: 12 }}>{e.time}</span>
                            </div>
                        ))
                    )}
                </Panel>

                <Panel title="Recent Notifications">
                    {data.notifications.length === 0 ? (
                        <p>No notifications found.</p>
                    ) : (
                        data.notifications.slice(0, 8).map((n, index) => (
                            <Row
                                key={n.id || index}
                                title={n.title || "Notification"}
                                subtitle={n.message || "�"}
                                status={n.isRead ? "Read" : "Unread"}
                                ok={n.isRead}
                            />
                        ))
                    )}
                </Panel>
            </div>
        </div>
    );
}

function isOnline(device) {
    if (!device.lastUpdated) return false;
    return Date.now() - new Date(device.lastUpdated).getTime() < 2 * 60 * 1000;
}

function Card({ title, value, color }) {
    return (
        <div style={{ ...cardStyle, borderTop: `6px solid ${color}` }}>
            <div style={{ color: "#666", fontSize: 14 }}>{title}</div>
            <div style={{ fontSize: 32, fontWeight: "bold", marginTop: 10 }}>{value}</div>
        </div>
    );
}

function Panel({ title, children }) {
    return (
        <div style={panelStyle}>
            <h3>{title}</h3>
            <div style={{ display: "grid", gap: 10 }}>{children}</div>
        </div>
    );
}

function Row({ title, subtitle, status, ok }) {
    return (
        <div style={rowStyle}>
            <div>
                <b>{title}</b>
                <br />
                <span style={{ color: "#777", fontSize: 13 }}>{subtitle}</span>
            </div>

            <span style={{ color: ok ? "green" : "red", fontWeight: "bold" }}>
                {status}
            </span>
        </div>
    );
}

const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginTop: 20,
};

const layoutStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: 20,
    marginTop: 24,
};

const cardStyle = {
    background: "white",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
};

const panelStyle = {
    background: "white",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
};

const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    borderBottom: "1px solid #eee",
    paddingBottom: 8,
};

const eventStyle = {
    borderBottom: "1px solid #eee",
    paddingBottom: 8,
};

export default GlobalMonitoringCenterPage;